import { useCallback, useEffect, useState } from "react";
import { Button, Icon, IconButton, useToast } from "@astrum/ui";
import { formatClock } from "@astrum/shared";
import type { TimeEntry } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { formatElapsed, liveMinutes } from "../lib/time.js";
import { LogTimeModal } from "./LogTimeModal.js";
import "./task-time.css";

/**
 * Time on a task: a start/stop timer, a way to log time by hand, and the
 * entries so far. There is one timer per person, so starting it here stops
 * whatever was running elsewhere (and says so).
 */

interface Props {
  taskId: string;
  taskTitle: string;
  /** Whether the viewer may log time here (the same rule as editing the task). */
  canLog: boolean;
}

const PAGE = 50;

/** Today in the viewer's own calendar, as the day key the server uses. */
const todayKey = () => new Date().toLocaleDateString("en-CA");

export function TaskTime({ taskId, taskTitle, canLog }: Props) {
  const { toast } = useToast();
  const { viewer } = useAuth();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [timer, setTimer] = useState<TimeEntry | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | undefined>();

  const fail = useCallback(
    (e: unknown, fallback: string) => toast(e instanceof ApiError ? e.message : fallback, { tone: "danger" }),
    [toast],
  );

  const reload = useCallback(async () => {
    try {
      const [list, current] = await Promise.all([
        api.get<{ entries: TimeEntry[]; nextCursor: string | null }>(`/time/entries?taskId=${taskId}&limit=${PAGE}`),
        api.get<{ timer: TimeEntry | null }>("/time/timer"),
      ]);
      setEntries(list.entries);
      setCursor(list.nextCursor);
      setTimer(current.timer);
    } catch (e) {
      fail(e, "Could not load time for this task.");
    }
  }, [taskId, fail]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Tick only while a timer is running, so an idle page does no work.
  const running = timer !== null;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  const here = timer?.taskId === taskId;

  const start = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ timer: TimeEntry; stopped: TimeEntry | null }>("/time/timer/start", { taskId });
      if (res.stopped) toast(`Stopped the timer on "${res.stopped.taskTitle ?? "another task"}" (${formatClock(res.stopped.minutes)}).`);
      await reload();
    } catch (e) {
      fail(e, "Could not start the timer.");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/time/timer/stop");
      await reload();
    } catch (e) {
      fail(e, "Could not stop the timer.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry: TimeEntry) => {
    try {
      await api.delete(`/time/entries/${entry.id}`);
      setEntries((list) => list.filter((e) => e.id !== entry.id));
    } catch (e) {
      fail(e, "Could not delete that entry.");
    }
  };

  const more = async () => {
    if (!cursor) return;
    try {
      const res = await api.get<{ entries: TimeEntry[]; nextCursor: string | null }>(
        `/time/entries?taskId=${taskId}&limit=${PAGE}&cursor=${cursor}`,
      );
      setEntries((list) => [...list, ...res.entries]);
      setCursor(res.nextCursor);
    } catch (e) {
      fail(e, "Could not load more.");
    }
  };

  const total = entries.reduce((n, e) => n + liveMinutes(e, now), 0);

  return (
    <div className="task-detail__section task-time">
      <div className="task-detail__section-header">
        <h3 className="task-detail__section-title">Time</h3>
        {entries.length > 0 && <span className="task-detail__subtask-progress">{formatClock(total)} logged</span>}
      </div>

      {canLog && (
        <div className="task-time__controls">
          {here && timer ? (
            <>
              <span className="task-time__clock" role="timer" aria-label="Time on this task">
                {formatElapsed(now - new Date(timer.startedAt!).getTime())}
              </span>
              <Button variant="secondary" size="sm" icon={<Icon name="close" size={14} />} onClick={() => void stop()} disabled={busy}>
                Stop
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" icon={<Icon name="clock" size={14} />} onClick={() => void start()} disabled={busy}>
              {timer ? "Switch timer to this task" : "Start timer"}
            </Button>
          )}
          <Button variant="ghost" size="sm" icon={<Icon name="plus" size={14} />} onClick={() => setLogOpen(true)}>
            Log time
          </Button>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="task-detail__hint">No time logged yet.</p>
      ) : (
        <ul className="task-time__list">
          {entries.map((entry) => {
            const mine = entry.userId === viewer?.id;
            return (
              <li key={entry.id} className="task-time__row">
                <span className="task-time__who">{mine ? "You" : (entry.userName ?? "Someone")}</span>
                <span className="task-time__day">{entry.date}</span>
                <span className="task-time__minutes">{entry.running ? `${formatElapsed(now - new Date(entry.startedAt!).getTime())} running` : formatClock(entry.minutes)}</span>
                <span className="task-time__note">{entry.note}</span>
                {mine && !entry.locked && !entry.running && (
                  <span className="task-time__actions">
                    <IconButton icon={<Icon name="edit" size={14} />} label="Edit entry" size="sm" onClick={() => setEditing(entry)} />
                    <IconButton icon={<Icon name="trash" size={14} />} label="Delete entry" size="sm" onClick={() => void remove(entry)} />
                  </span>
                )}
                {mine && entry.locked && <Icon name="lock" size={14} aria-label="Week submitted" />}
              </li>
            );
          })}
        </ul>
      )}
      {cursor && (
        <Button variant="ghost" size="sm" onClick={() => void more()}>
          Show more
        </Button>
      )}

      <LogTimeModal
        open={logOpen || editing !== undefined}
        onClose={() => {
          setLogOpen(false);
          setEditing(undefined);
        }}
        onSaved={() => void reload()}
        today={todayKey()}
        defaultDate={todayKey()}
        task={{ id: taskId, title: taskTitle }}
        entry={editing}
      />
    </div>
  );
}
